import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Circle, 
  ArrowRight, 
  TrendingUp, 
  AlertCircle,
  Sparkles,
  Shield
} from 'lucide-react';
import { getCompanyProfileHealth, getFunderProfileHealth } from '@/lib/api';

export function ProfileHealth({ userRole }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const data = userRole === 'COMPANY' 
          ? await getCompanyProfileHealth()
          : await getFunderProfileHealth();
        setHealth(data);
      } catch (error) {
        console.error('Error fetching profile health:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHealth();
  }, [userRole]);

  if (loading) {
    return (
      <Card className="border-slate-200 animate-pulse">
        <CardContent className="p-6">
          <div className="h-24 bg-slate-100 rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!health) {
    return null;
  }

  const { total_score, health_level, health_color, breakdown, recommendations } = health;

  // Perfect score celebration
  if (total_score >= 100) {
    return (
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-100 rounded-full animate-pulse">
              <Sparkles className="h-8 w-8 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-emerald-800">Profile Health: Excellent!</h3>
              <p className="text-emerald-600">Your profile is fully optimized and ready to attract the best opportunities.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Shield className="h-5 w-5 text-slate-600" />
            Profile Health Score
          </CardTitle>
          <div 
            className="text-2xl font-bold" 
            style={{ color: health_color }}
            data-testid="profile-health-score"
          >
            {total_score}%
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overall Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Overall Score</span>
            <span 
              className="text-sm font-medium px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${health_color}20`, color: health_color }}
            >
              {health_level}
            </span>
          </div>
          <Progress 
            value={total_score} 
            className="h-3"
            style={{ '--progress-color': health_color }}
          />
        </div>

        {/* Breakdown */}
        {breakdown && Object.keys(breakdown).length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-500 uppercase">Score Breakdown</p>
            <div className="grid gap-2">
              {Object.entries(breakdown).map(([key, data]) => (
                <div key={key} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    {data.score === data.max ? (
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Circle className="h-4 w-4 text-slate-300" />
                    )}
                    <span className="text-slate-700">{data.label}</span>
                  </div>
                  <span className="text-slate-500">{data.score}/{data.max}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recommendations && recommendations.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-500 uppercase flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Recommendations to Improve
            </p>
            <ul className="space-y-1">
              {recommendations.map((rec, index) => (
                <li key={index} className="text-sm text-slate-600 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  {rec}
                </li>
              ))}
            </ul>
            <Link to={userRole === 'COMPANY' ? '/company/onboarding' : '/funder/onboarding'}>
              <Button variant="link" className="px-0 h-auto text-blue-600 text-sm">
                Complete your profile
                <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ProfileHealth;
